import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import useStore from "../../hooks/store/useStore";
import useEventTracking from "../../services/EventTracking/useEventTracking";
import { MenuTypeMapping } from "../../services/EventTracking/mapping";
import cn from "../../utils/cn";
import { MenuItemType } from "../../types";
import Text from "../ui/Text";
import Touchable from "../ui/Touchable";
import buildMenuItems from "./utils/buildMenuItems";
import scrollMenuItem from "./utils/scrollMenuItem";

export default function Menu({
  selectedMenuItem,
  setSelectedMenuItem,
}: {
  selectedMenuItem: MenuItemType;
  setSelectedMenuItem: Dispatch<SetStateAction<MenuItemType>>;
}) {
  const { t } = useTranslation();

  const [canScroll, setCanScroll] = useState<boolean>(true);

  const navRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const { source } = useStore((state) => state.global);
  const { userInformation } = useStore((state) => state.user);
  const { market, trialUserSegmentation } = userInformation || {};

  const mutation = useEventTracking();
  const menuItems = useMemo(
    () => buildMenuItems(market, source, trialUserSegmentation),
    [market, source, trialUserSegmentation],
  );

  useEffect(() => {
    scrollMenuItem(
      menuRef,
      selectedMenuItem,
      menuItems.map(({ type }) => type),
    );
  }, [selectedMenuItem, menuItems]);

  useEffect(() => {
    const updateCanScroll = () => {
      if (!menuRef.current || !navRef.current) return;

      setCanScroll(menuRef.current.scrollWidth > navRef.current.clientWidth);
    };

    updateCanScroll();
    window.addEventListener("resize", updateCanScroll);

    return () => window.removeEventListener("resize", updateCanScroll);
  }, [menuItems]);

  const navStyle = canScroll
    ? "hide-scrollbar overflow-x-auto"
    : "flex justify-center";

  return (
    <nav ref={navRef} className={cn(navStyle, "w-full min-w-0")}>
      <ul ref={menuRef} className="flex w-fit gap-1">
        {menuItems.map(({ type, icon }) => {
          const selectedStyle = type === selectedMenuItem ? "bg-pink2" : null;

          return (
            <Touchable
              key={type}
              className={cn(
                "flex flex-none items-center gap-1 rounded-3xl px-3 py-2 duration-300 ease-in-out",
                selectedStyle,
              )}
              onClick={() => {
                mutation.mutate({
                  pageId: "Experience Menu",
                  eventId: MenuTypeMapping[type],
                });
                setSelectedMenuItem(type);
              }}
            >
              <img className="size-6" src={icon} alt={type} />

              <Text className="text-nowrap">{t(type)}</Text>
            </Touchable>
          );
        })}
      </ul>
    </nav>
  );
}
